import * as React from 'react';

import {Button} from 'shells/button/button';

import {AiButtonContent} from '../power-ai/ai-button-content';
import {useGenerateEntitySummary} from '../power-ai/timeline-summary/graphql/hooks/use-generate-entity-summary';

import {useGetActivity} from './graphql/hooks/use-get-activity';
import {useSaveTranscriptSummary} from './graphql/hooks/use-save-transcript-summary';
import {TranscriptSummary} from './transcript-summary';

type Props = {
    activityId: string;
};

export function ActivityModalSummarizeButton(props: Props) {
    const [isShowingTranscriptSummary, setIsShowingTranscriptSummary] = React.useState<any>();
    const [summarySaved, setSummarySaved] = React.useState<boolean>(false);

    const {saveTranscriptSummary} = useSaveTranscriptSummary();
    const {generateEntitySummary} = useGenerateEntitySummary();
    const buttonRef = React.useRef(null);
    const {activity} = useGetActivity(props.activityId);

    const externalMeeting = activity && activity.externalMeeting;
    const logNote = activity && activity.logNote;

    let transcriptionSummary;
    let entityId;
    // Moving forward, we will always use the activity summary no matter kind of activity
    if (NutClientConfig.hasZoomTwoPointOhFF) {
        transcriptionSummary = activity && activity.summary;
        entityId = activity && activity.id;
    } else {
        transcriptionSummary = externalMeeting
            ? externalMeeting.transcriptionSummary
            : activity && activity.summary;
        entityId = externalMeeting ? externalMeeting.id : activity && activity.id;
    }

    const hasTranscription = activity && activity.transcription;

    // need to check for '0' because hasTranscription resolves to 0 as a string
    if (!hasTranscription && hasTranscription !== '0') {
        return null;
    }

    const handleSummarize = () => {
        if (!activity || !entityId) return;
        generateEntitySummary({entityId, summaryType: 'CALL'}, undefined);
    };

    const closeSummary = () => setIsShowingTranscriptSummary(false);
    const refreshActivityModalAndCloseSummary = (updatedLogNote?: string | null) => {
        // Trigger to update the activity modal with updated log note
        $(window).trigger('activityModalLogNoteUpdated', [props.activityId, updatedLogNote]);
        closeSummary();
    };

    const onSaveTranscriptSummary = (replace: boolean) =>
        saveTranscriptSummary(props.activityId, replace).then((mutationResults) => {
            const updatedLogNote =
                mutationResults.data &&
                mutationResults.data.transcriptSummarySave &&
                mutationResults.data.transcriptSummarySave.activity &&
                mutationResults.data.transcriptSummarySave.activity.logNote &&
                mutationResults.data.transcriptSummarySave.activity.logNote.bodyHtml;

            refreshActivityModalAndCloseSummary(updatedLogNote);
            setSummarySaved(true);
        });

    if (summarySaved) {
        return null;
    }

    return (
        <>
            {isShowingTranscriptSummary && entityId ? (
                <TranscriptSummary
                    entityId={entityId}
                    onAdd={() => onSaveTranscriptSummary(false)}
                    // @ts-expect-error
                    onReplace={logNote ? () => onSaveTranscriptSummary(true) : undefined}
                    onCancel={closeSummary}
                />
            ) : (
                <>
                    {/* Only show button if we aren't auto-summarizing */}
                    {!(activity && activity.shouldSummarize) && (
                        <div>
                            <Button
                                variant='ai-blue'
                                getButtonRef={buttonRef}
                                onClick={(e) => {
                                    e.stopPropagation();

                                    if (!transcriptionSummary && activity) {
                                        handleSummarize();
                                    }
                                    if (activity) {
                                        setIsShowingTranscriptSummary(true);
                                    }
                                }}
                            >
                                <AiButtonContent
                                    buttonRef={buttonRef}
                                    buttonText='Summarize transcript'
                                />
                            </Button>
                        </div>
                    )}
                </>
            )}
        </>
    );
}
